import html2pdf from 'html2pdf.js';

const EXPORT_IGNORE_SELECTOR = '[data-export-ignore]';
const OVERFLOW_CONTAINER_SELECTOR = [
  '[class*="measurement-table-section"]',
  '[class*="-charts-scroll"]',
  '[class*="table-wrap"]',
  '[class*="chart-area"]'
].join(', ');

const EXPORT_PRINT_STYLES = `
  [data-export-ignore] { display: none !important; }
  [class*="measurement-table-section"],
  [class*="-charts-scroll"],
  [class*="table-wrap"],
  [class*="chart-area"] {
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
  }
  table {
    width: max-content !important;
    min-width: 100% !important;
  }
`;

function expandOverflowContainers(root) {
  const restores = new Map();

  const remember = (el) => {
    if (restores.has(el)) return;
    restores.set(el, {
      overflow: el.style.overflow,
      overflowX: el.style.overflowX,
      overflowY: el.style.overflowY,
      maxHeight: el.style.maxHeight,
      maxWidth: el.style.maxWidth,
      width: el.style.width,
      minWidth: el.style.minWidth
    });
  };

  root.querySelectorAll('*').forEach((el) => {
    const style = window.getComputedStyle(el);
    const hasHorizontalOverflow = style.overflowX === 'auto' || style.overflowX === 'scroll';
    const hasVerticalOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';

    if (hasHorizontalOverflow || hasVerticalOverflow) {
      remember(el);
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
      el.style.maxHeight = 'none';
    }
  });

  root.querySelectorAll('table').forEach((table) => {
    remember(table);
    table.style.width = 'max-content';
    table.style.minWidth = '100%';
  });

  remember(root);
  const fullWidth = Math.max(root.scrollWidth, root.offsetWidth, root.clientWidth);
  if (fullWidth > root.clientWidth) {
    root.style.width = `${fullWidth}px`;
    root.style.maxWidth = 'none';
  }

  return () => {
    restores.forEach((styles, el) => {
      el.style.overflow = styles.overflow;
      el.style.overflowX = styles.overflowX;
      el.style.overflowY = styles.overflowY;
      el.style.maxHeight = styles.maxHeight;
      el.style.maxWidth = styles.maxWidth;
      el.style.width = styles.width;
      el.style.minWidth = styles.minWidth;
    });
  };
}

function prepareExportClone(clonedDoc, rootId) {
  const root = rootId ? clonedDoc.getElementById(rootId) : clonedDoc.body;
  if (!root) return;

  root.querySelectorAll(EXPORT_IGNORE_SELECTOR).forEach((el) => {
    el.style.display = 'none';
  });

  root.querySelectorAll('*').forEach((el) => {
    const style = clonedDoc.defaultView.getComputedStyle(el);
    const hasHorizontalOverflow = style.overflowX === 'auto' || style.overflowX === 'scroll';
    const hasVerticalOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';

    if (hasHorizontalOverflow || hasVerticalOverflow) {
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
      el.style.maxHeight = 'none';
    }
  });

  root.querySelectorAll(OVERFLOW_CONTAINER_SELECTOR).forEach((el) => {
    el.style.overflow = 'visible';
    el.style.overflowX = 'visible';
    el.style.overflowY = 'visible';
    el.style.maxHeight = 'none';
  });

  root.querySelectorAll('table').forEach((table) => {
    table.style.width = 'max-content';
    table.style.minWidth = '100%';
  });

  const width = Math.max(root.scrollWidth, root.offsetWidth);
  if (width > 0) {
    root.style.width = `${width}px`;
    root.style.maxWidth = 'none';
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportElementToPdf(element, filename, options = {}) {
  if (!element) throw new Error('Export element not found');

  const elementId = element.id || null;
  const userOnClone = options.html2canvas?.onclone;
  const restoreExpanded = expandOverflowContainers(element);
  const captureWidth = Math.max(element.scrollWidth, element.offsetWidth, element.clientWidth);

  try {
    const html2canvasOpts = {
      scale: 2,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: captureWidth,
      width: captureWidth,
      ...(options.html2canvas || {}),
      onclone: (clonedDoc) => {
        prepareExportClone(clonedDoc, elementId);
        userOnClone?.(clonedDoc);
      }
    };

    const opt = {
      margin: [0.2, 0.2, 0.2, 0.2],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: html2canvasOpts,
      jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
      pagebreak: { mode: ['css', 'legacy'] },
      ...options,
      html2canvas: html2canvasOpts
    };

    await html2pdf().set(opt).from(element).save();
  } finally {
    restoreExpanded();
  }
}

export function printElement(element, title = 'Report') {
  if (!element) throw new Error('Print element not found');

  const w = window.open('', '_blank');
  if (!w) throw new Error('Popup blocked');

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');

  w.document.open();
  w.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>${title}</title>
        ${styles}
        <style>
          ${EXPORT_PRINT_STYLES}
          @media print {
            body { background: #fff !important; }
            .btn-back-shared { display: none !important; }
          }
        </style>
      </head>
      <body>
        ${element.outerHTML}
      </body>
    </html>
  `);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 250);
}

export function toCsv(rows) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return rows.map((r) => r.map(escape).join(',')).join('\n');
}
